import type { LogoutOptions } from "@auth0/auth0-react";
import { FullStory, isInitialized } from "@fullstory/browser";

const FS_UID_KEY = "fs_uid";

/**
 * FullStory capture is only safe once the current visitor is in a known
 * state for this session: either identified as the current user, or freshly
 * rotated to a new anonymous visitor. Until then we may still be attached to
 * a previous user's persisted visitor cookie, so route-based capture must
 * stay off. We track this reactively (rather than re-reading localStorage on
 * demand) so gating components re-render the moment identity is stamped —
 * the previous localStorage-only read never re-triggered React, which left
 * capture shut down whenever identity was confirmed after the gate last ran.
 */
type CaptureState = "unknown" | "identified" | "anonymous";

const readInitialCaptureState = (): CaptureState => {
  try {
    // A persisted uid means this visitor was already identified on a prior
    // load, so capture is safe immediately on refresh/deep-link.
    return localStorage.getItem(FS_UID_KEY) ? "identified" : "unknown";
  } catch {
    return "unknown";
  }
};

let captureState: CaptureState = readInitialCaptureState();
const identityListeners = new Set<() => void>();

const setCaptureState = (next: CaptureState): void => {
  if (next === captureState) return;
  captureState = next;
  identityListeners.forEach((listener) => listener());
};

/**
 * Subscribe to FullStory identity / capture-safety changes. Returns an
 * unsubscribe function. Designed for `useSyncExternalStore`.
 */
export const subscribeFullStoryIdentity = (
  listener: () => void
): (() => void) => {
  identityListeners.add(listener);
  return () => {
    identityListeners.delete(listener);
  };
};

/**
 * True once the FullStory visitor is safe to capture without misattributing
 * activity to a previous user — i.e. we have identified the current user or
 * rotated the visitor to a fresh anonymous profile this session.
 */
export const isFullStoryCaptureSafe = (): boolean => captureState !== "unknown";

/**
 * Rotates the FullStory visitor so a new authenticated user does not
 * inherit the previous user's identity, captured form text, or
 * `setProperties` values (e.g. `email`). FullStory's visitor cookie and
 * any properties attached via `setProperties({ type: "user" })` persist
 * across logouts; without `anonymize` a follow-up `setIdentity` only
 * relabels the same visitor, leaking the prior user's metadata into the
 * new session.
 */
export const anonymizeFullStorySession = (): void => {
  try {
    if (isInitialized()) {
      // V2 API: `setIdentity({ anonymous: true })` rotates the
      // FullStory visitor; the deprecated `FS.anonymize()` is the
      // legacy equivalent.
      FullStory("setIdentity", { anonymous: true });
    }
  } catch {
    // FullStory is best-effort; never block app flow on it
  }
  try {
    localStorage.removeItem(FS_UID_KEY);
  } catch {
    // localStorage can throw in restricted contexts (Safari private mode)
  }
  // Visitor is now a fresh anonymous profile — safe to capture even before
  // a follow-up setIdentity. Mark outside the try blocks so a localStorage
  // failure does not leave the gate stuck.
  setCaptureState("anonymous");
};

export const readLastFullStoryUid = (): string | null => {
  try {
    return localStorage.getItem(FS_UID_KEY);
  } catch {
    return null;
  }
};

export const rememberFullStoryUid = (uid: string): void => {
  try {
    localStorage.setItem(FS_UID_KEY, uid);
  } catch {
    // ignore
  }
  // The caller has just identified this user via setIdentity, so capture is
  // safe now regardless of whether the localStorage write succeeded.
  setCaptureState("identified");
};

export type FullStoryUserProperties = {
  email?: string | null;
  tenantportUserId?: string | null;
};

/**
 * Identify the current Auth0 user. One uid everywhere (the Auth0 `sub`):
 * Landing used to stamp the TenantPort user id, and the register page only
 * stamped while it was already on screen. After signup the browser returns
 * on the application form, which never identified, so capture stayed shut
 * down for every wizard — conventional, affordable, co-applicant, and
 * live-in aide.
 *
 * A different uid rotates the visitor first so the previous user's email
 * and captured form text do not carry over.
 */
export const identifyFullStoryUser = (
  uid: string,
  properties?: FullStoryUserProperties
): void => {
  if (!uid) return;
  if (readLastFullStoryUid() !== uid) {
    anonymizeFullStorySession();
  }
  try {
    if (isInitialized()) {
      FullStory("setIdentity", { uid });
      const userProperties: Record<string, string> = {};
      if (properties?.email) userProperties.email = properties.email;
      if (properties?.tenantportUserId) {
        userProperties.tenantportUserId = properties.tenantportUserId;
      }
      if (Object.keys(userProperties).length > 0) {
        FullStory("setProperties", {
          type: "user",
          properties: userProperties,
        });
      }
    }
  } catch {
    // FullStory is best-effort; never block app flow on it
  }
  rememberFullStoryUid(uid);
};

/**
 * Applicant wizards all live under `/application`. Prefix match so a new
 * wizard step is recorded without another allowlist edit.
 *
 * This is also the quota boundary. Home, rent, requests, settings,
 * availability, property, and renewal must not match — capture on those
 * routes would record every resident session.
 */
export const isApplicationCapturePath = (pathname: string): boolean =>
  pathname === "/application" || pathname.startsWith("/application/");

/**
 * Logout wrapper that rotates the FullStory visitor before handing off
 * to Auth0. Use everywhere we sign a user out so the next user on the
 * same browser starts on a fresh FullStory visitor profile.
 */
export const safeLogout = (
  logout: (options?: LogoutOptions) => Promise<void> | void,
  options?: LogoutOptions
): Promise<void> | void => {
  anonymizeFullStorySession();
  return logout(options);
};
